import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FIELD } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { PhotoAnnotator } from "@/components/PhotoAnnotator";
import { FieldAiSuggestions } from "@/components/FieldAiSuggestions";
import { VoiceNoteButton } from "@/components/VoiceNoteButton";
import { compressImage } from "@/lib/image";
import { enqueue } from "@/lib/offlineQueue";
import { flushQueue, pendingCount } from "@/lib/fieldSync";
import { fmtDate } from "@/lib/format";
import {
  ArrowLeft, HardHat, ShieldCheck, MapPin, Camera, Plus, Trash2, Send,
  CheckCircle2, Sprout, Phone, WifiOff, Droplets, PencilLine, Sparkles, Loader2, AlertTriangle, CloudOff,
} from "lucide-react";
import { toast } from "sonner";

const SAFETY_ITEMS = [
  { k: "boots", label: "Safety boots & gloves on", icon: ShieldCheck },
  { k: "vest", label: "High-visibility vest on", icon: ShieldCheck },
  { k: "hydration", label: "Hydration & sun protection", icon: Droplets },
  { k: "weather", label: "Checked weather / lightning risk", icon: ShieldCheck },
];

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export default function FieldWorkPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [safetyDone, setSafetyDone] = useState(false);
  const [checks, setChecks] = useState({});
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [cap, setCap] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [pairForm, setPairForm] = useState({ before: "", after: "", caption: "" });
  const [pairBusy, setPairBusy] = useState(false);
  const [pendCount, setPendCount] = useState(0);

  const load = useCallback(() => api.get("/work-orders/today").then((r) => setTasks(r.data)).catch(() => {}), []);
  const refreshPending = useCallback(async () => { try { setPendCount(await pendingCount()); } catch {} }, []);

  useEffect(() => { load(); }, [load]);

  // Offline sync: flush on load and whenever the connection returns.
  useEffect(() => {
    const sync = async (announce) => {
      const r = await flushQueue();
      await refreshPending();
      if (r.flushed > 0) { load(); if (announce) toast.success(`${r.flushed} offline submission${r.flushed > 1 ? "s" : ""} uploaded`); }
    };
    refreshPending();
    sync(false);
    const onOnline = () => sync(true);
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [load, refreshPending]);

  const allChecked = SAFETY_ITEMS.every((s) => checks[s.k]);

  const openTask = (t) => {
    setActive(t);
    setCap({ quantities: [{ item: "", quantity: "", unit: "sqm" }], labour: [{ role: "", hours: "", workers: 1 }], photos: [], pairs: [], safety_notes: "", notes: "" });
    setPairForm({ before: "", after: "", caption: "" });
    setAi(null);
  };

  const photoByLocal = (localId) => cap?.photos.find((p) => p.localId === localId);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    let dataUrl;
    try { dataUrl = await compressImage(f); }
    catch { toast.error("Couldn't read that photo. Try taking it again."); return; }
    setAi(null);
    setPending({ dataUrl, caption: "" });
    analyzePhoto(dataUrl);
  };

  const analyzePhoto = async (dataUrl) => {
    if (!active) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/analyze-photo", { work_order_id: active.id, data_url: dataUrl }, { timeout: 60000 });
      setAi(data);
    } catch {
      // AI assist is non-blocking; the worker can still mark up & keep the photo.
    } finally {
      setAiBusy(false);
    }
  };

  const applyCaption = (c) => setPending((p) => (p ? { ...p, caption: c } : p));
  const applyNotes = (n) => setCap((c) => ({ ...c, notes: c.notes ? `${c.notes} ${n}`.trim() : n }));
  const addQuantities = (rows) => setCap((c) => ({
    ...c,
    quantities: [...c.quantities.filter((r) => r.item), ...rows.map((q) => ({ item: q.item, quantity: q.quantity ? String(q.quantity) : "", unit: q.unit || "sqm" }))],
  }));
  const addLabour = (rows) => setCap((c) => ({
    ...c,
    labour: [...c.labour.filter((r) => r.role), ...rows.map((l) => ({ role: l.role, hours: l.hours ? String(l.hours) : "", workers: l.workers || 1 }))],
  }));
  const applyAll = () => {
    if (!ai) return;
    if (ai.caption) applyCaption(ai.caption);
    if (ai.notes) applyNotes(ai.notes);
    if (ai.quantities?.length) addQuantities(ai.quantities);
    if (ai.labour?.length) addLabour(ai.labour);
    toast.success("AI suggestions applied");
  };

  // Local-first: a captured photo is held on-device and uploaded at submit (works offline).
  const addCapturedPhoto = async (dataUrl, caption, annotated, originalDataUrl) => {
    let lat = null, lng = null;
    try {
      const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 2000 }));
      lat = pos.coords.latitude; lng = pos.coords.longitude;
    } catch {}
    const quality = ai?.quality || null;
    const photo = { localId: uid(), data_url: dataUrl, caption, annotated, original_data_url: annotated ? originalDataUrl : null, lat, lng, quality };
    setCap((c) => ({ ...c, photos: [...c.photos, photo] }));
    if (quality && quality.flag && quality.flag !== "ok") toast.warning(quality.message || "Photo flagged for quality — supervisor will review");
    else toast.success(annotated ? "Marked-up photo added" : "Photo added");
    setPending(null);
    setAi(null);
  };

  const savePair = () => {
    if (!pairForm.before || !pairForm.after || pairForm.before === pairForm.after) { toast.error("Pick two different photos"); return; }
    setCap((c) => ({ ...c, pairs: [...(c.pairs || []), { localId: uid(), beforeLocalId: pairForm.before, afterLocalId: pairForm.after, caption: pairForm.caption }] }));
    setPairForm({ before: "", after: "", caption: "" });
    toast.success("Before / after pair saved");
  };

  const suggestPair = async () => {
    setPairBusy(true);
    try {
      const { data } = await api.post("/ai/suggest-pair", { work_order_id: active.id, photos: cap.photos.map((p) => ({ key: p.localId, data_url: p.data_url })) }, { timeout: 60000 });
      setPairForm({ before: data.before_key, after: data.after_key, caption: data.caption || "" });
      toast.success("AI picked a before / after pair — review & save");
    } catch (e) { toast.error(apiErr(e, "Couldn't suggest a pair")); }
    finally { setPairBusy(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    const item = {
      id: uid(),
      work_order_id: active.id,
      work_order_title: active.title,
      payload: {
        safety_notes: cap.safety_notes,
        quantities: cap.quantities.filter((q) => q.item).map((q) => ({ item: q.item, quantity: Number(q.quantity) || 0, unit: q.unit || "unit" })),
        labour: cap.labour.filter((l) => l.role).map((l) => ({ role: l.role, hours: Number(l.hours) || 0, workers: Number(l.workers) || 1 })),
        notes: cap.notes,
      },
      photos: cap.photos.map((p) => ({ localId: p.localId, data_url: p.data_url, caption: p.caption, annotated: !!p.annotated, original_data_url: p.original_data_url || null, lat: p.lat ?? null, lng: p.lng ?? null, quality: p.quality || null })),
      pairs: (cap.pairs || []).map((pr) => ({ beforeLocalId: pr.beforeLocalId, afterLocalId: pr.afterLocalId, caption: pr.caption || null })),
      created_at: new Date().toISOString(),
    };
    try {
      await enqueue(item);
      if (navigator.onLine !== false) {
        const r = await flushQueue();
        await refreshPending();
        toast.success(r.pending === 0 ? "Work submitted" : "Saved — finishing upload in the background");
      } else {
        await refreshPending();
        toast.success("Saved offline — it'll upload automatically when you're back online");
      }
      setActive(null); setCap(null); load();
    } catch {
      toast.error("Couldn't save your work. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Safety gate ----
  if (!safetyDone) {
    return (
      <div className="min-h-screen bg-forest-deep pb-10 text-emerald-50" data-testid={FIELD.root}>
        <div className="mx-auto max-w-md p-4">
          <button onClick={() => navigate("/")} className="mb-4 flex items-center gap-1.5 text-sm text-emerald-200" data-testid={FIELD.backBtn}>
            <ArrowLeft className="h-4 w-4" /> Exit field mode
          </button>
          <div className="flex items-center gap-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage-accent"><HardHat className="h-6 w-6" /></div>
            <div><p className="font-heading text-xl font-bold">Today's Work</p><p className="text-xs text-emerald-200/70">{user?.name}</p></div>
          </div>
          <div className="mt-6 rounded-2xl bg-white p-5 text-forest-dark">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><ShieldCheck className="h-5 w-5 text-sage-accent" /> Daily safety check-in</h2>
            <p className="mt-1 text-sm text-muted-foreground">Confirm before you start on site.</p>
            <div className="mt-4 space-y-2">
              {SAFETY_ITEMS.map((s) => (
                <label key={s.k} className="flex items-center gap-3 rounded-xl border p-4 active:bg-sage-surface">
                  <Checkbox className="h-6 w-6" checked={!!checks[s.k]} onCheckedChange={(v) => setChecks((c) => ({ ...c, [s.k]: v }))} />
                  <s.icon className="h-5 w-5 text-forest-mid" />
                  <span className="text-base font-medium">{s.label}</span>
                </label>
              ))}
            </div>
            <Button disabled={!allChecked} onClick={() => setSafetyDone(true)} data-testid={FIELD.safetyBtn}
              className="mt-5 h-14 w-full rounded-xl bg-forest-mid text-base font-bold hover:bg-forest-dark">
              <CheckCircle2 className="mr-2 h-5 w-5" /> Start my shift
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Task list ----
  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid={FIELD.root}>
      <header className="sticky top-0 z-20 flex items-center justify-between bg-forest-deep px-4 py-3 text-emerald-50">
        <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm" data-testid={FIELD.backBtn}><ArrowLeft className="h-4 w-4" /> Exit</button>
        <div className="flex items-center gap-1.5"><Sprout className="h-4 w-4" /><span className="font-heading font-bold">Today's Work</span></div>
        {pendCount > 0 ? (
          <span className="flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-semibold text-white" data-testid={FIELD.pendingBadge}>
            <CloudOff className="h-3 w-3" /> {pendCount} pending
          </span>
        ) : (
          <span className="flex items-center gap-1 text-[10px] text-emerald-200/70"><WifiOff className="h-3 w-3" /> offline-ready</span>
        )}
      </header>

      <div className="mx-auto max-w-md space-y-4 p-4">
        <div className="flex items-center gap-2 rounded-xl bg-emerald-100 p-3 text-sm text-emerald-900">
          <ShieldCheck className="h-5 w-5" /> Safety check complete. Stay hydrated.
        </div>

        {tasks.length === 0 && (
          <div className="rounded-2xl border border-dashed bg-white p-10 text-center text-sm text-muted-foreground">
            No tasks assigned to you right now.
          </div>
        )}

        {tasks.map((t) => (
          <button key={t.id} onClick={() => openTask(t)} data-testid={FIELD.taskCard(t.id)}
            className="block w-full rounded-2xl border bg-white p-4 text-left shadow-sm transition-transform active:scale-[0.98]">
            <div className="flex items-start justify-between gap-2">
              <p className="font-heading text-lg font-bold text-forest-dark">{t.title}</p>
              <StatusBadge status={t.status} />
            </div>
            <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {t.site_name}</span>
              <span>{fmtDate(t.scheduled_date)}</span>
            </div>
            <div className="mt-3 flex items-center gap-1 text-sm font-semibold text-forest-mid">Tap to capture work →</div>
          </button>
        ))}

        <a href="tel:995" className="flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700">
          <Phone className="h-4 w-4" /> Emergency call
        </a>
      </div>

      {/* capture dialog */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid={FIELD.captureDialog}>
          {active && cap && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{active.title}</DialogTitle>
                <DialogDescription>Record quantities, labour, photos and notes for this task.</DialogDescription>
              </DialogHeader>
              <div className="space-y-5">
                {/* quantities */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <Label className="text-base">Quantities done</Label>
                    <Button size="sm" variant="outline" onClick={() => setCap((c) => ({ ...c, quantities: [...c.quantities, { item: "", quantity: "", unit: "sqm" }] }))} data-testid={FIELD.addQty}><Plus className="h-4 w-4" /></Button>
                  </div>
                  {cap.quantities.map((q, i) => (
                    <div key={i} className="mb-2 grid grid-cols-[1fr,70px,70px,auto] gap-2">
                      <Input className="h-11" placeholder="Item" value={q.item} onChange={(e) => setCap((c) => { const a = [...c.quantities]; a[i] = { ...a[i], item: e.target.value }; return { ...c, quantities: a }; })} />
                      <Input className="h-11" type="number" placeholder="Qty" value={q.quantity} onChange={(e) => setCap((c) => { const a = [...c.quantities]; a[i] = { ...a[i], quantity: e.target.value }; return { ...c, quantities: a }; })} />
                      <Input className="h-11" placeholder="Unit" value={q.unit} onChange={(e) => setCap((c) => { const a = [...c.quantities]; a[i] = { ...a[i], unit: e.target.value }; return { ...c, quantities: a }; })} />
                      <Button variant="ghost" size="icon" className="h-11 w-9 text-rose-500" onClick={() => setCap((c) => ({ ...c, quantities: c.quantities.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>

                {/* labour */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <Label className="text-base">Labour</Label>
                    <Button size="sm" variant="outline" onClick={() => setCap((c) => ({ ...c, labour: [...c.labour, { role: "", hours: "", workers: 1 }] }))} data-testid={FIELD.addLabour}><Plus className="h-4 w-4" /></Button>
                  </div>
                  {cap.labour.map((l, i) => (
                    <div key={i} className="mb-2 grid grid-cols-[1fr,70px,70px,auto] gap-2">
                      <Input className="h-11" placeholder="Role" value={l.role} onChange={(e) => setCap((c) => { const a = [...c.labour]; a[i] = { ...a[i], role: e.target.value }; return { ...c, labour: a }; })} />
                      <Input className="h-11" type="number" placeholder="Hrs" value={l.hours} onChange={(e) => setCap((c) => { const a = [...c.labour]; a[i] = { ...a[i], hours: e.target.value }; return { ...c, labour: a }; })} />
                      <Input className="h-11" type="number" placeholder="Qty" value={l.workers} onChange={(e) => setCap((c) => { const a = [...c.labour]; a[i] = { ...a[i], workers: e.target.value }; return { ...c, labour: a }; })} />
                      <Button variant="ghost" size="icon" className="h-11 w-9 text-rose-500" onClick={() => setCap((c) => ({ ...c, labour: c.labour.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>

                {/* photos */}
                <div>
                  <Label className="text-base">Photos</Label>
                  <label className="mt-2 flex h-16 cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-forest-mid/40 bg-sage-surface text-sm font-semibold text-forest-mid active:bg-sage-light">
                    <Camera className="h-5 w-5" /> Capture / upload photo
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid={FIELD.photoInput} />
                  </label>
                  {cap.photos.length > 0 && (
                    <div className="mt-2 grid grid-cols-4 gap-2">
                      {cap.photos.map((p) => (
                        <div key={p.localId} className="relative">
                          <img src={p.data_url} alt="" className="h-16 w-full rounded-lg object-cover" />
                          {p.annotated && <span className="absolute right-0 top-0 rounded-bl bg-terracotta px-1 text-[8px] text-white"><PencilLine className="inline h-2 w-2" /></span>}
                          {p.quality?.flag && p.quality.flag !== "ok" && <span className="absolute right-0 bottom-0 rounded-tl bg-amber-500 px-1 text-[8px] text-white" title={p.quality.message || p.quality.flag}><AlertTriangle className="inline h-2 w-2" /></span>}
                          {p.lat != null && <span className="absolute bottom-0 left-0 rounded-tr bg-black/60 px-1 text-[8px] text-white"><MapPin className="inline h-2 w-2" /> GPS</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {cap.photos.length >= 2 && (
                  <div className="rounded-xl border border-sage-light bg-sage-surface p-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base">Before / after pair</Label>
                      <Button type="button" size="sm" variant="outline" className="h-8" onClick={suggestPair} disabled={pairBusy} data-testid={FIELD.suggestPairBtn}>
                        {pairBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5 text-forest-mid" />} Suggest with AI
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Tag two photos to show the improvement side-by-side in reports & tenders.</p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Select value={pairForm.before} onValueChange={(v) => setPairForm({ ...pairForm, before: v })}>
                        <SelectTrigger className="h-10" data-testid={FIELD.pairBefore}><SelectValue placeholder="Before" /></SelectTrigger>
                        <SelectContent>{cap.photos.map((p, i) => <SelectItem key={p.localId} value={p.localId}>Photo {i + 1}{p.caption ? ` · ${p.caption}` : ""}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={pairForm.after} onValueChange={(v) => setPairForm({ ...pairForm, after: v })}>
                        <SelectTrigger className="h-10" data-testid={FIELD.pairAfter}><SelectValue placeholder="After" /></SelectTrigger>
                        <SelectContent>{cap.photos.map((p, i) => <SelectItem key={p.localId} value={p.localId}>Photo {i + 1}{p.caption ? ` · ${p.caption}` : ""}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input className="mt-2 h-10" placeholder="Caption (optional)" value={pairForm.caption} onChange={(e) => setPairForm({ ...pairForm, caption: e.target.value })} />
                    <Button type="button" onClick={savePair} className="mt-2 w-full bg-sage-accent hover:bg-sage-accent/90" data-testid={FIELD.pairSave}>Save before / after</Button>
                    {(cap.pairs || []).length > 0 && (
                      <div className="mt-3 space-y-2">
                        {cap.pairs.map((pr) => (
                          <div key={pr.localId} className="grid grid-cols-2 gap-2">
                            <div className="relative"><img src={photoByLocal(pr.beforeLocalId)?.data_url} className="h-20 w-full rounded-lg object-cover" alt="" /><span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[8px] font-semibold text-white">BEFORE</span></div>
                            <div className="relative"><img src={photoByLocal(pr.afterLocalId)?.data_url} className="h-20 w-full rounded-lg object-cover" alt="" /><span className="absolute left-1 top-1 rounded bg-forest-mid px-1 text-[8px] font-semibold text-white">AFTER</span></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Notes</Label>
                    <VoiceNoteButton onResult={applyNotes} />
                  </div>
                  <Textarea value={cap.notes} onChange={(e) => setCap((c) => ({ ...c, notes: e.target.value }))} rows={2} placeholder="Type, or tap Voice note to speak" />
                </div>

                <Button onClick={submit} disabled={submitting} data-testid={FIELD.submitBtn}
                  className="h-14 w-full rounded-xl bg-forest-mid text-base font-bold hover:bg-forest-dark active:scale-[0.98]">
                  <Send className="mr-2 h-5 w-5" /> Submit completed work
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pending} onOpenChange={(v) => { if (!v) { setPending(null); setAi(null); } }}>
        <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid={FIELD.annotateDialog}>
          <DialogHeader>
            <DialogTitle className="font-heading">Document &amp; mark up photo</DialogTitle>
            <DialogDescription>Review AI suggestions, mark up the photo, then add it to your work.</DialogDescription>
          </DialogHeader>
          {pending && (
            <div className="space-y-3">
              <FieldAiSuggestions
                ai={ai}
                busy={aiBusy}
                onApplyCaption={applyCaption}
                onApplyNotes={applyNotes}
                onAddQuantities={addQuantities}
                onAddLabour={addLabour}
                onApplyAll={applyAll}
              />
              <PhotoAnnotator
                src={pending.dataUrl}
                suggestions={ai?.annotations}
                onSkip={() => addCapturedPhoto(pending.dataUrl, pending.caption, false)}
                onSave={(url) => addCapturedPhoto(url, pending.caption, true, pending.dataUrl)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
